import React, { useState, useEffect } from 'react';
import '../styles/CAUpgradeQueue.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  try {
    const saved = localStorage.getItem("auditpros_user");
    if (saved) {
      const parsed  = JSON.parse(saved);
      const account = parsed?.account;
      const email = account?.idTokenClaims?.email
        || account?.idTokenClaims?.preferred_username
        || account?.idTokenClaims?.emails?.[0]
        || account?.username
        || account?.preferred_username
        || account?.email;
      const name = account?.name
        || account?.idTokenClaims?.name
        || account?.idTokenClaims?.given_name
        || account?.username?.split('@')[0]
        || "";
      const headers = {};
      if (email) headers["x-user-email"] = email;
      if (name)  headers["x-user-name"]  = name;
      return headers;
    }
  } catch { /* ignore */ }
  return {};
}

function formatRequestId(num) {
  return `R${String(num || 0).padStart(8, '0')}`;
}

function StatusBadge({ status }) {
  const config = {
    pending:  { label: 'Pending',  color: 'var(--amber)', bg: 'var(--amber-bg)', border: 'rgba(245,166,35,0.3)' },
    approved: { label: 'Approved', color: 'var(--green)', bg: 'var(--green-bg)', border: 'rgba(62,207,122,0.3)' },
    rejected: { label: 'Rejected', color: 'var(--red)',   bg: 'var(--red-bg)',   border: 'rgba(224,82,82,0.3)'  },
  };
  const c = config[status] || config.pending;
  return (
    <span
      className="caq-status-badge"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}` }}
    >
      {c.label}
    </span>
  );
}

function RequestCard({ request, onAction }) {
  const [rejecting,  setRejecting]  = useState(false);
  const [reason,     setReason]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [localError, setLocalError] = useState('');

  const docs = Array.isArray(request.document_urls)
    ? request.document_urls
    : JSON.parse(request.document_urls || '[]');

  const handleApprove = async () => {
    setLoading(true); setLocalError('');
    try {
      const res = await fetch(`${BASE}/users/upgrade-requests/${request.id}/approve`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      onAction(request.id, 'approved');
    } catch (err) {
      setLocalError(err.message);
    } finally { setLoading(false); }
  };

  const handleReject = async () => {
    setLoading(true); setLocalError('');
    try {
      const res = await fetch(`${BASE}/users/upgrade-requests/${request.id}/reject`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      onAction(request.id, 'rejected');
    } catch (err) {
      setLocalError(err.message);
    } finally { setLoading(false); setRejecting(false); }
  };

  return (
    <div className="caq-card">
      <div className="caq-card-header">
        <div>
          <div className="caq-request-id">{formatRequestId(request.request_number)}</div>
          <div className="caq-name">{request.full_name}</div>
          <div className="caq-email">{request.email}</div>
        </div>
        <StatusBadge status={request.status} />
      </div>

      <div className="caq-details-grid">
        <div>
          <div className="caq-detail-label">ICAI Number</div>
          <div className="caq-detail-value--bold">{request.icai_member_id}</div>
        </div>
        <div>
          <div className="caq-detail-label">Submitted</div>
          <div className="caq-detail-value">
            {new Date(request.submitted_at).toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </div>
        </div>
        {request.reviewed_at && (
          <div>
            <div className="caq-detail-label">Reviewed by</div>
            <div className="caq-detail-value">
              {request.reviewed_by_name} · {new Date(request.reviewed_at).toLocaleDateString('en-IN')}
            </div>
          </div>
        )}
        {request.rejection_reason && (
          <div className="caq-detail-full">
            <div className="caq-detail-label">Rejection reason</div>
            <div className="caq-rejection-value">{request.rejection_reason}</div>
          </div>
        )}
      </div>

      {docs.length > 0 && (
        <div className="caq-docs">
          <div className="caq-docs-label">Documents</div>
          <div className="caq-docs-list">
            {docs.map((doc, i) => (
              <a key={i} href={doc.url} target="_blank" rel="noreferrer" className="caq-doc-link">
                📄 {doc.name}
              </a>
            ))}
          </div>
        </div>
      )}

      {localError && <div className="caq-local-error">{localError}</div>}

      {request.status === 'pending' && (
        <div>
          {rejecting ? (
            <div>
              <textarea
                placeholder="Reason for rejection (optional but recommended)"
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                className="caq-reject-textarea"
              />
              <div className="caq-actions">
                <button onClick={handleReject} disabled={loading} className="caq-btn-reject-confirm">
                  {loading ? 'Rejecting…' : 'Confirm Rejection'}
                </button>
                <button onClick={() => { setRejecting(false); setReason(''); }} className="caq-btn-cancel">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="caq-actions">
              <button onClick={handleApprove} disabled={loading} className="caq-btn-approve">
                {loading ? 'Approving…' : '✓ Approve'}
              </button>
              <button onClick={() => setRejecting(true)} disabled={loading} className="caq-btn-reject">
                ✕ Reject
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CAUpgradeQueue({ userRole }) {
  const [requests,    setRequests]    = useState([]);
  const [filter,      setFilter]      = useState('pending');
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [pageSize,    setPageSize]    = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const loadRequests = async () => {
    setLoading(true); setError(''); setCurrentPage(1);
    try {
      const statusParam = search ? '' : `?status=${filter}`;
      const res  = await fetch(
        `${BASE}/users/upgrade-requests${statusParam}`,
        { headers: getAuthHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load requests');
      setRequests(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRequests(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = search.trim()
    ? requests.filter(r => {
        const q = search.trim().toLowerCase();
        return (
          formatRequestId(r.request_number).toLowerCase().includes(q) ||
          (r.full_name      || '').toLowerCase().includes(q) ||
          (r.email          || '').toLowerCase().includes(q) ||
          (r.icai_member_id || '').toLowerCase().includes(q) ||
          (r.status         || '').toLowerCase().includes(q)
        );
      })
    : requests;

  const totalPages    = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIdx      = (currentPage - 1) * pageSize;
  const endIdx        = Math.min(startIdx + pageSize, filtered.length);
  const visibleRows   = filtered.slice(startIdx, endIdx);

  const handleAction = (requestId, newStatus) => {
    setRequests(prev => prev.map(r =>
      r.id === requestId ? { ...r, status: newStatus } : r
    ));
    if (filter === 'pending') {
      setRequests(prev => prev.filter(r => r.id !== requestId));
    }
  };

  if (!userRole?.isAdmin) {
    return <div className="caq-unauthorized">Admin access required.</div>;
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="caq-page">
      <div className="caq-page-header">
        <h2 className="caq-title">
          CA Upgrade Requests
          {filter === 'pending' && pendingCount > 0 && (
            <span className="caq-pending-badge">{pendingCount}</span>
          )}
        </h2>
        <button onClick={loadRequests} className="caq-btn-refresh">↺ Refresh</button>
      </div>

      <div className="caq-search-wrap">
        <span className="caq-search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search by request ID, name, email, ICAI number"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="caq-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')} className="caq-search-clear">×</button>
        )}
      </div>

      {!search && (
        <div className="caq-filter-tabs">
          {['pending', 'approved', 'rejected'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`caq-filter-btn${filter === f ? ' caq-filter-btn--active' : ''}`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {search && (
        <div className="caq-search-count">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
        </div>
      )}

      {error && <div className="caq-error-banner">{error}</div>}

      {!loading && filtered.length > 0 && (
        <div className="caq-pagination-toolbar">
          <div className="caq-page-size">
            <span>Show</span>
            <select
              className="caq-page-size-select"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </div>
          <span className="caq-results-count">
            {startIdx + 1}–{endIdx} of {filtered.length}
          </span>
        </div>
      )}

      {loading ? (
        <div className="caq-loading">Loading requests…</div>
      ) : filtered.length === 0 ? (
        <div className="caq-empty">
          {search ? `No results found for "${search}".` : `No ${filter} requests.`}
        </div>
      ) : (
        <>
          {visibleRows.map(r => (
            <RequestCard key={r.id} request={r} onAction={handleAction} />
          ))}
          {totalPages > 1 && (
            <div className="caq-pagination">
              <button
                className="caq-page-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ← Prev
              </button>
              <span className="caq-page-info">Page {currentPage} of {totalPages}</span>
              <button
                className="caq-page-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
